import { AppShell, PageHeader } from '../components/AppShell'
import { Callout, DataRow, Panel } from '../components/ui'
import { usePortal } from '../lib/portal-context'
import { CheckCircle2, CircleSlash } from 'lucide-react'

export function SettingsPage() {
  const { config, user } = usePortal()

  return (
    <AppShell>
      <div className="mx-auto max-w-[900px] px-4 py-7 sm:px-6">
        <PageHeader
          eyebrow="Configuration"
          title="Settings"
          blurb="These values come from the .env file next to the server. Edit that file and restart the portal to change them. User accounts are managed under Users."
        />

        <div className="space-y-5">
          <Panel title="Signed-in account">
            <DataRow label="Name">{user?.name || '—'}</DataRow>
            <DataRow label="Email">{user?.email || '—'}</DataRow>
            <DataRow label="Permission level">
              {config?.roles.find((role) => role.code === user?.role)?.label ?? user?.role ?? '—'}
            </DataRow>
          </Panel>

          <Panel title="Community">
            <DataRow label="Community">{config?.community}</DataRow>
            <DataRow label="Management company">{config?.management}</DataRow>
            <DataRow label="Public base URL">
              <code className="font-mono text-[12.5px] text-altair-300">{config?.publicBaseUrl}</code>
            </DataRow>
          </Panel>

          <Panel title="Report recipients">
            <DataRow label="To">
              {config?.hoaTo.length ? config.hoaTo.join(', ') : 'Not set — add HOA_TO in .env'}
            </DataRow>
            <DataRow label="CC">{config?.hoaCc.length ? config.hoaCc.join(', ') : '—'}</DataRow>
            {!config?.hoaTo.length ? (
              <Callout tone="warn" className="mt-3">
                Without a default recipient you can still send reports. You just type the address into
                the email dialog each time.
              </Callout>
            ) : null}
          </Panel>

          <Panel title="Capabilities">
            <Capability
              on={Boolean(config?.smtpEnabled)}
              label="Direct email sending (SMTP)"
              onText="Configured — reports send straight from the portal."
              offText="Not configured — the portal hands a prefilled draft to your mail app instead."
            />
            <Capability
              on={Boolean(config?.mediaToolsAvailable)}
              label="Video thumbnails and metadata (ffmpeg)"
              onText="Available — posters, duration, and resolution are extracted automatically."
              offText="Not found — install ffmpeg to get video poster frames."
            />
            <Capability
              on={Boolean(config?.authRequired)}
              label="Account sign-in"
              onText="Enabled — the dashboard requires a portal account. Case report links still open with their share token."
              offText="Off."
            />
          </Panel>

          <Panel title="Default reporter">
            <DataRow label="Name">{config?.reporter.name || '—'}</DataRow>
            <DataRow label="Email">{config?.reporter.email || '—'}</DataRow>
            <DataRow label="Phone">{config?.reporter.phone || '—'}</DataRow>
            <DataRow label="Address">{config?.reporter.address || '—'}</DataRow>
            <p className="mt-3 text-[12.5px] leading-relaxed text-slate-500">
              These prefill every new report so you are not retyping your contact details. Signed-in
              name and email are used when those fields are blank.
            </p>
          </Panel>
        </div>
      </div>
    </AppShell>
  )
}

function Capability({
  on,
  label,
  onText,
  offText,
}: {
  on: boolean
  label: string
  onText: string
  offText: string
}) {
  return (
    <div className="flex items-start gap-3 border-b border-white/[0.05] py-3 last:border-0">
      {on ? (
        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-400" />
      ) : (
        <CircleSlash size={16} className="mt-0.5 shrink-0 text-slate-600" />
      )}
      <div>
        <div className="text-[13.5px] font-medium text-slate-200">{label}</div>
        <div className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500">
          {on ? onText : offText}
        </div>
      </div>
    </div>
  )
}
